import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';

import { AuthService } from 'src/app/services/auth.service';
import { ReportBannerService } from 'src/app/services/report-banner.service';

import { AppState } from 'src/app/state/app.state';
import { ModalComponent } from '../../../common/modal/modal.component';
import { FileUploadService } from 'src/app/file-upload.service';

@Component({
  selector: 'app-bulk-upload',
  standalone: true,
  imports: [FormsModule, NgIf, ReactiveFormsModule, NgClass, ModalComponent],
  templateUrl: './bulk-upload.component.html',
  styleUrl: './bulk-upload.component.scss',
})
export class BulkUploadComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() showModal: boolean = false;
  @Output() modalClosed: EventEmitter<void> = new EventEmitter();
  user: any;
  isEmailsSelected: any = false;
  isExcelSelected: any = false;
  batchType: string = 'email'; // Default batch type is email

  isUploadExcelModalOpen: any = false;
  isPasteEmailModalOpen: boolean;
  isUploadExcelSuccesModalOpen: any = false;
  isUploadExcelFailedModalOpen: boolean;
  isPasteEmailFailedModalOpen: boolean;
  isPasteEmailSuccesModalOpen: boolean;
  showUploadExcelModal: boolean;
  showPasteEmailsModal: boolean;
  showUploadExcelModalSuccess: boolean;
  showPasteEmailsModalSuccess: boolean;
  showUploadExcelModalFailed: boolean;
  showPasteEmailsModalFailed: boolean;
  pasteEmailsInput: any = '';
  pasteEmailsPlaceholder: any =
    'email1@example.com\nemail2@example.com\nemail3@example.com ';
  processedEmails: string[] = [];
  uploadExcelErrorMessage: any = '';
  MAX_FILE_SIZE_MB: number = 10;
  uploadedExcelFileName: string;
  excelNumberOfLinkedinUrls: number;
  uploadedEmailsCount: number = 0;
  uploadProgress: number = -1;
  uploadExcelErrorModalMessage: any = '';

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private fileUploadService: FileUploadService,
    private reportBannerService: ReportBannerService
  ) {}

  errorMessage = '';
  ngOnInit(): void {
    this.authService.authUser().subscribe((user) => {
      this.user = user;
    });
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    // Ensure we turn off bulk mode when component is destroyed
    // Use forceDisable to avoid closing any active report banners during navigation
    this.reportBannerService.forceDisableBulkUploadMode();
  }

  selectExcel() {
    this.isExcelSelected = !this.isExcelSelected;
    this.isEmailsSelected
      ? (this.isEmailsSelected = !this.isEmailsSelected)
      : '';
  }

  selectPasteEmails() {
    this.isEmailsSelected = !this.isEmailsSelected;
    this.isExcelSelected ? (this.isExcelSelected = !this.isExcelSelected) : '';
  }

  selectBatchType(type: string) {
    this.batchType = type;
  }

  isDragging = false;
  file: File | null = null;

  modalTitle = 'SalesOMMO for Events & Webinars ';
  modalTitleColor = '#FF5733';
  modalButtons = [
    {
      text: 'Close',
      class: 'btn-cancel',
      isDisabled: false,
      action: () => this.closeBulkUploadModal(),
    },
    {
      text: 'Continue',
      class: 'btn-blue',
      isDisabled: () => !this.isExcelSelected && !this.isEmailsSelected,
      action: () =>
        this.isExcelSelected
          ? this.openExcelUploadModal()
          : this.openPasteEmailsModal(),
    },
  ];

  uploadExcelModalTitle = 'Upload an excel list ';
  uploadExcelModalTitleColor = 'text-black';
  uploadExcelModalButtons = [
    {
      text: 'Cancel',
      class: 'btn-cancel',
      isDisabled: false,
      action: () => {
        this.closeExcelUploadModal(), this.openBulkUploadModal();
      },
    },
    {
      text: 'Upload',
      class: 'btn-blue',
      isDisabled: () => !this.file,
      action: () => {
        if (this.file) {
          this.closeExcelUploadModal();
          this.openUploadExcelLoadingModal();
          this.uploadCSVFile(this.file);
        }
      },
    },
  ];

  pasteEmailsModalTitle = 'Paste email addresses ';
  pasteEmailsModalTitleColor = 'text-black';
  pasteEmailsModalButtons = [
    {
      text: 'Cancel',
      class: 'btn-cancel',
      isDisabled: false,
      action: () => {
        this.closePasteEmailsModal(), this.openBulkUploadModal();
      },
    },
    {
      text: 'Proceed',
      class: 'btn-blue',
      isDisabled: () => !this.pasteEmailsInput || this.pasteEmailsInput.length === 0,
      action: () => this.uploadPastedEmails(),
    },
  ];

  uploadExcelModalSuccessTitle = 'Your file was uploaded successfully! ';
  uploadExcelModalSuccessTitleColor = 'text-[#4BB543]';
  uploadExcelModalSuccessButtons = [
    {
      text: 'OK',
      class: 'btn-blue',
      isDisabled: false,
      action: () => {
        this.closeExcelUploadModalSuccess();
      },
    },
  ];

  uploadExcelModalFailedTitle = 'Oops! There was an issue with your upload. ';
  uploadExcelModalFailedTitleColor = 'text-[#FF3A2D]';
  uploadExcelModalFailedButtons = [
    {
      text: 'Cancel',
      class: 'btn-cancel',
      isDisabled: false,
      action: () => this.closeExcelUploadModalFailed(),
    },
    {
      text: 'Try again',
      class: 'btn-blue',
      isDisabled: false,
      action: () => {
        this.closeExcelUploadModalFailed(), this.openExcelUploadModal();
      },
    },
  ];

  pasteEmailsModalSuccessTitle = 'Your emails were uploaded successfully! ';
  pasteEmailsModalSuccessTitleColor = 'text-[#4BB543]';
  pasteEmailsModalSuccessButtons = [
    {
      text: 'Go Back',
      class: 'btn-cancel',
      isDisabled: false,
      action: () => this.closePasteEmailsModalSuccess(),
    },
    {
      text: 'OK',
      class: 'btn-blue',
      isDisabled: false,
      action: () => this.closePasteEmailsModalSuccess(),
    },
  ];

  pasteEmailsModalFailedTitle = 'Oops! There was an issue with your upload. ';
  pasteEmailsModalFailedTitleColor = 'text-[#FF3A2D]';
  pasteEmailsModalFailedButtons = [
    {
      text: 'Go Back',
      class: 'btn-cancel',
      isDisabled: false,
      action: () => this.closePasteEmailsModalFailed(),
    },
    {
      text: 'Try again',
      class: 'btn-blue',
      isDisabled: false,
      action: () => {
        this.closePasteEmailsModalFailed();
        this.openPasteEmailsModal();
      },
    },
  ];

  showUploadExcelLoadingModal: boolean = false;
  showUploadExcelLoadingModalTitle = 'Uploading Data ';
  showUploadExcelLoadingModalTitleColor = 'text-black';
  showUploadExcelLoadingModalButtons = [];

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    if (event.dataTransfer?.files.length) {
      const droppedFile = event.dataTransfer.files[0];
      if (droppedFile.type === 'text/csv') {
        this.uploadExcelErrorMessage = '';
        this.file = droppedFile;
        this.uploadedExcelFileName = this.file.name;
      } else {
        this.uploadExcelErrorMessage = 'Please upload a valid .csv file';
      }
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const selectedFile = input.files[0];
      if (selectedFile.type === 'text/csv') {
        this.uploadExcelErrorMessage = '';
        this.file = selectedFile;
        this.uploadedExcelFileName = this.file.name;
      } else {
        this.uploadExcelErrorMessage = 'Please upload a valid .csv file';
      }
    }
  }

  @ViewChild('fileInput') fileInput!: ElementRef;

  downloadCSV(): void {
    this.fileUploadService.downloadCSV(this.batchType);
  }

  triggerFileInput(): void {
    // Reset the file input value
    const inputElement = this.fileInput.nativeElement;
    inputElement.value = ''; // Clear the input to allow the same file to be selected again

    // Trigger the file input click
    inputElement.click();
  }

  uploadCSVFile(file: File): void {
    // Enable bulk upload mode to show single banner
    this.reportBannerService.setBulkUploadMode(true);

    this.fileUploadService.handleCSVUpload(file, this.batchType)
      .then((response) => {
        if (response.success) {
          // Handle different property names based on batch type
          if (this.batchType === 'linkedin') {
            this.excelNumberOfLinkedinUrls = response.data.processing_result.total_urls || 0;
          } else {
            // For email type, check both possible property names
            this.excelNumberOfLinkedinUrls =
              response.data.processing_result.total_emails ||
              response.data.processing_result.total_urls ||
              response.count ||
              0;
          }

          this.closeUploadExcelLoadingModal();
          this.openExcelUploadModalSuccess();
          // Banner is already shown by ReportBannerService
        } else {
          this.uploadExcelErrorModalMessage = response.message;
          this.closeUploadExcelLoadingModal();
          this.openExcelUploadModalFailed();
          // Turn off bulk mode since upload failed
          this.reportBannerService.setBulkUploadMode(false);
        }
      })
      .catch((error) => {
        this.uploadExcelErrorModalMessage = error.message;
        this.closeUploadExcelLoadingModal();
        this.openExcelUploadModalFailed();
        // Turn off bulk mode since upload failed
        this.reportBannerService.setBulkUploadMode(false);
      });
  }

  uploadPastedEmails(): void {
    if (this.processEmails()) {
      // Make sure we have emails to upload
      if (this.processedEmails.length === 0) {
        this.errorMessage = 'No valid email addresses found.';
        return;
      }

      console.log('[BulkUploadComponent] Uploading', this.processedEmails.length, 'emails');

      // Enable bulk upload mode to show single banner
      this.reportBannerService.setBulkUploadMode(true);

      this.closePasteEmailsModal();
      this.openUploadExcelLoadingModal(); // Reuse the loading modal

      this.fileUploadService.handleEmailsUpload(this.processedEmails)
        .then((response) => {
          console.log('[BulkUploadComponent] Email upload response:', response);

          if (response.success) {
            this.uploadedEmailsCount = response.count || this.processedEmails.length;
            this.closeUploadExcelLoadingModal();
            this.openPasteEmailsModalSuccess();
          } else {
            this.errorMessage = response.message || 'An error occurred during email upload';
            this.closeUploadExcelLoadingModal();
            this.openPasteEmailsModalFailed();
            // Turn off bulk mode since upload failed
            this.reportBannerService.setBulkUploadMode(false);
          }
        })
        .catch((error) => {
          console.error('[BulkUploadComponent] Email upload error:', error);
          this.errorMessage = error.message || 'An unexpected error occurred during email upload';
          this.closeUploadExcelLoadingModal();
          this.openPasteEmailsModalFailed();
          // Turn off bulk mode since upload failed
          this.reportBannerService.setBulkUploadMode(false);
        });
    }
  }

  closeBulkUploadModal() {
    this.showModal = false;
    this.modalClosed.emit();
  }

  openBulkUploadModal() {
    this.showModal = true;
  }

  openExcelUploadModal() {
    this.uploadExcelErrorMessage = '';
    this.file = null;
    this.showModal = false;
    this.showUploadExcelModal = true;
  }

  closeExcelUploadModal() {
    this.showUploadExcelModal = false;
    // this.modalClosed.emit();
  }

  openPasteEmailsModal() {
    this.errorMessage = '';
    this.pasteEmailsInput = '';
    this.showModal = false;
    this.showPasteEmailsModal = true;
  }

  closePasteEmailsModal() {
    this.showPasteEmailsModal = false;
    // this.modalClosed.emit();
  }

  openExcelUploadModalSuccess() {
    this.showUploadExcelModal = false;
    this.showUploadExcelModalSuccess = true;
  }

  closeExcelUploadModalSuccess() {
    this.showUploadExcelModalSuccess = false;
    this.modalClosed.emit();
  }

  openExcelUploadModalFailed() {
    this.showUploadExcelModal = false;
    this.showUploadExcelModalFailed = true;
  }

  closeExcelUploadModalFailed() {
    this.showUploadExcelModalFailed = false;
    this.modalClosed.emit();
  }

  openPasteEmailsModalSuccess() {
    this.showPasteEmailsModal = false;
    this.showPasteEmailsModalSuccess = true;
  }

  closePasteEmailsModalSuccess() {
    this.pasteEmailsInput = '';
    this.processedEmails = [];
    this.showPasteEmailsModalSuccess = false;
    this.modalClosed.emit();
  }

  openPasteEmailsModalFailed() {
    this.showPasteEmailsModal = false;
    this.showPasteEmailsModalFailed = true;
  }

  closePasteEmailsModalFailed() {
    this.pasteEmailsInput = '';
    this.processedEmails = [];
    this.showPasteEmailsModalFailed = false;
    this.modalClosed.emit();
  }

  openUploadExcelLoadingModal() {
    this.showUploadExcelModal = false;
    this.showPasteEmailsModal = false;
    this.showUploadExcelLoadingModal = true;
  }

  closeUploadExcelLoadingModal() {
    this.showUploadExcelLoadingModal = false;
  }

  /**
   * Process pasted emails and validate them
   */
  processEmails(): boolean {
    console.log('[BulkUploadComponent] Processing emails from input:', this.pasteEmailsInput);

    // Guard against empty input
    if (!this.pasteEmailsInput || this.pasteEmailsInput.trim() === '') {
      this.errorMessage = 'Please enter at least one email address.';
      return false;
    }

    const emailArray = this.pasteEmailsInput
      .split('\n')
      .map((email: string) => email.trim())
      .filter((email: string) => email !== '');

    console.log('[BulkUploadComponent] Parsed email array:', emailArray);

    // Check if we have any emails
    if (emailArray.length === 0) {
      this.errorMessage = 'Please enter at least one email address.';
      return false;
    }

    // Check if any email is invalid
    const invalidEmails = emailArray.filter(
      (email: string) => !this.isValidEmail(email)
    );

    console.log('[BulkUploadComponent] Invalid emails found:', invalidEmails);

    if (invalidEmails.length > 0) {
      this.errorMessage = `The following emails are invalid: ${invalidEmails.join(
        ', '
      )}`;
      return false;
    }

    const validEmails = [...new Set(emailArray)] as string[]; // Explicitly cast to string[]
    console.log('[BulkUploadComponent] Final processed emails:', validEmails);

    this.processedEmails = validEmails;
    this.errorMessage = '';
    return true;
  }

  emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  isValidEmail(email: string): boolean {
    return this.emailRegex.test(email);
  }

  // Add this method to your BulkUploadComponent class

  /**
   * Removes the currently selected file
   */
  removeFile(): void {
    this.file = null;
    this.uploadedExcelFileName = null;
    this.uploadExcelErrorMessage = '';

    // Clear the file input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
